import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Upload,
  Settings,
  CreditCard,
  User,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { SEO, SEOConfigs } from "@/components/SEO";
import { Footer } from "@/components/layout/Footer";

interface DocumentationSection {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  content: React.ReactNode;
}

export function Documentation() {
  const [activeSection, setActiveSection] = useState<string>("getting-started");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["getting-started"])
  );

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const sections: DocumentationSection[] = [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: ArrowRight,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-3">
              Welcome to SnapLading
            </h3>
            <p className="text-gray-600 mb-4">
              SnapLading is an AI agent platform that converts bill of lading
              documents into EDI X12 204/211 format automatically. This guide
              will walk you through all features and functionality.
            </p>
            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Video Placeholder</h4>
                  <p className="text-sm text-blue-700">
                    Insert a 2-3 minute overview video showing the complete workflow from upload to EDI generation
                  </p>
                </div>
              </div>
            </div> */}
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Account Creation & Login
            </h4>
            <div className="space-y-4">
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Visit the SnapLading homepage</li>
                <li>
                  Click "Sign Up" to create a new account or "Sign In" if you
                  already have one
                </li>
                <li>Complete the authentication process</li>
                <li>
                  Upon first login, your account will be automatically
                  initialized
                </li>
              </ol>
              {/* <div className="rounded-lg overflow-hidden border border-gray-200">
                <img
                  src="/images/docs/landing_signin_signup.jpg"
                  alt="Landing page with Sign In and Sign Up buttons highlighted"
                  className="w-full h-auto"
                />
                <img
                  src="/images/docs/signin_signup.jpg"
                  alt="Sign In and Sign Up"
                  className="w-full h-auto"
                />
              </div> */}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">First-Time Setup</h4>
            <p className="text-gray-600 mb-3">
              When you first log in, SnapLading will automatically set up your
              account environment. You'll see a brief initialization screen.
            </p>
            {/* <div className="rounded-lg overflow-hidden border border-gray-200">
              <img
                src="/images/docs/first_time_setup.jpg"
                alt="First-Time Setup"
                className="w-full h-auto"
              />
            </div> */}
          </div>
        </div>
      ),
    },
    {
      id: "upload",
      title: "Upload Documents",
      icon: Upload,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-3">
              Uploading Bill of Lading Images
            </h3>
            <p className="text-gray-600 mb-4">
              The Upload page is where you submit your bill of lading documents
              for processing. SnapLading supports multiple image formats and
              batch processing.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Supported File Formats
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {["JPEG", "PNG", "BMP", "TIFF", "WebP"].map((format) => (
                <Badge
                  key={format}
                  variant="outline"
                  className="justify-center py-2"
                >
                  {format}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Upload Methods</h4>
            <div className="space-y-4">
              {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src="/images/docs/upload.jpg"
                      alt="Upload page showing the drag-and-drop zone with sample files"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div> */}
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h5 className="font-medium">Drag & Drop</h5>
                    <p className="text-sm text-gray-600">
                      Drag files directly into the upload zone
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h5 className="font-medium">Click to Browse</h5>
                    <p className="text-sm text-gray-600">
                      Click the upload zone to open file browser
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Interchange IDs at Upload
            </h4>
            <div className="space-y-3">
              <p className="text-gray-600">
                Choose the EDI Interchange Sender and Receiver IDs directly on
                the Upload page before submitting your documents:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                <li>
                  Use the Interchange Sender ID and Interchange Receiver ID
                  dropdowns to select from your saved IDs
                </li>
                <li>
                  If you have no saved IDs, defaults are applied (SNAPLADING)
                </li>
                <li>
                  Each ID is limited to a maximum of 15 characters (longer
                  values are truncated and the API will reject invalid inputs)
                </li>
                <li>
                  Selected IDs are sent with the upload and used in EDI
                  generation
                </li>
                <li>
                  After processing, the chosen IDs appear in the Documents page
                  under Sender ID and Receiver ID
                </li>
              </ol>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Processing States</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
                <div>
                  <h5 className="font-medium text-yellow-900">Processing</h5>
                  <p className="text-sm text-yellow-700">
                    Your documents are being analyzed by our AI
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <h5 className="font-medium text-green-900">Completed</h5>
                  <p className="text-sm text-green-700">
                    Processing successful - view results in Documents page
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <div>
                  <h5 className="font-medium text-red-900">
                    Failed [No charge]
                  </h5>
                  <p className="text-sm text-red-700">
                    Processing failed [No charge] - click "Show Details" or
                    "Reprocess" to try again
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Error Handling</h4>
            <div className="space-y-3">
              <p className="text-gray-600">
                If processing fails, you'll see error notifications with options
                to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>
                  <strong>Show Details:</strong> View detailed error information
                </li>
                <li>
                  <strong>Reprocess:</strong> Retry processing the same document
                </li>
                <li>
                  <strong>Upload Again:</strong> Upload a higher quality version
                </li>
              </ul>
            </div>
          </div>

          {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Video Placeholder</h4>
                <p className="text-sm text-blue-700">
                  Screen recording showing the complete upload process: selecting files, uploading, and viewing processing status
                </p>
              </div>
            </div>
          </div> */}
        </div>
      ),
    },
    {
      id: "documents",
      title: "Documents & Results",
      icon: FileText,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-3">
              Viewing Processed Documents
            </h3>
            <p className="text-gray-600 mb-4">
              The Documents page shows all your processed bills of lading with
              their status, extracted data, and generated EDI files.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Document List View</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src="/images/docs/documents.jpg"
                    alt="Documents page showing the bills of lading being processed and their state"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h5 className="font-medium">Table Columns:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>
                      <strong>Timestamp:</strong> When the document was
                      processed
                    </li>
                    <li>
                      <strong>Thumbnails:</strong> Preview images of the
                      original documents
                    </li>
                    <li>
                      <strong>Shipper Name:</strong> Extracted shipper
                      information
                    </li>
                    <li>
                      <strong>Sender ID:</strong> Interchange sender used for
                      EDI generation
                    </li>
                    <li>
                      <strong>Receiver ID:</strong> Interchange receiver used
                      for EDI generation
                    </li>
                    <li>
                      <strong>EDI Type:</strong> Generated document type (e.g.,
                      204 or 211)
                    </li>
                    <li>
                      <strong>ISA:</strong> Control number or BOL reference
                    </li>
                    <li>
                      <strong>Details:</strong> Button to view full details
                    </li>
                    <li>
                      <strong>Status:</strong> Processing status with icons
                    </li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h5 className="font-medium">Status Icons:</h5>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Completed</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RefreshCw className="h-4 w-4 text-blue-600" />
                      <span className="text-sm">Processing</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <span className="text-sm">Failed [No charge]</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Document Details Modal
            </h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Document details modal showing extracted data sections,
                    full-size images, and EDI output
                  </p>
                </div>
              </div>
            </div> */}
            <p className="text-gray-600 mb-3">
              Click "Details" on any document to view:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h5 className="font-medium">Extracted Information:</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Shipper details (name, address)</li>
                  <li>• Consignee details (name, address)</li>
                  <li>• Shipment details (weight, pieces, commodity)</li>
                  <li>• Reference numbers (BOL, PO, ISA)</li>
                  <li>• Pickup and delivery dates</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h5 className="font-medium">Additional Data:</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Full-size original images</li>
                  <li>• Generated EDI X12 204/211 output</li>
                  <li>• Validation errors and warnings</li>
                  <li>• Missing fields report</li>
                  <li>• Confidence scores</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Image Viewer</h4>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src="/images/docs/documents_details.jpg"
                    alt="Documents page showing the details of a bill of lading being processed and their state"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Click on any thumbnail to view the full-size image. For multi-page
              documents, use the navigation dots at the bottom.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Pagination & Search</h4>
            <p className="text-gray-600 mb-3">
              The Documents page supports pagination (20 documents per page) and
              automatically polls for updates on in-progress documents.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">
                    Video Placeholder
                  </h4>
                  <p className="text-sm text-blue-700">
                    Screen recording showing navigation through the Documents
                    page, opening details modal, and viewing images
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "settings",
      title: "Settings & Configuration",
      icon: Settings,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-3">Account Settings</h3>
            <p className="text-gray-600 mb-4">
              The Settings page allows you to configure SFTP access, EDI
              document preferences, and pricing plans.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              SFTP Credentials - No Account State
            </h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    SFTP section showing "No SFTP Account" message with "Create
                    SFTP Account" button
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">
                When you first visit Settings, you'll see:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Server icon and "No SFTP Account" message</li>
                <li>Explanation that SFTP is needed to access EDI files</li>
                <li>"Create SFTP Account" button</li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              SFTP Credentials - Account Created State
            </h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    SFTP section showing credentials grid with username, server,
                    port, and password fields, plus account status
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">
                After creating an SFTP account, you'll see:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Credential Fields:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Username (with copy button)</li>
                    <li>• FTP Server Address (with copy button)</li>
                    <li>• Port number</li>
                    <li>• Password (with show/hide toggle and copy button)</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Account Status:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Active/Inactive badge</li>
                    <li>• Account creation date</li>
                    <li>• Last login timestamp</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Password Management</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Password update section showing new password field with
                    show/hide toggle and validation requirements
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">To update your SFTP password:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                <li>Enter a new password (minimum 10 characters)</li>
                <li>Password must include uppercase, lowercase, and numbers</li>
                <li>Click "Update Password" to save</li>
                <li>Use the eye icon to show/hide password while typing</li>
              </ol>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              EDI Document Settings
            </h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    EDI settings section showing checkboxes for document types,
                    X12 version dropdown, timezone selector, and receiver ID
                    field
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">
                Configure your EDI output preferences:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Document Types:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>☑ EDI 204 (Motor Carrier Load Tender)</li>
                    <li>☑ EDI 211 (Motor Carrier Bill of Lading)</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Configuration:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• X12 Version: 4010, 5010, or 7010</li>
                    <li>• Timezone selection</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Saved Interchange IDs
            </h4>
            <div className="space-y-3">
              <p className="text-gray-600">
                Manage reusable Interchange IDs that appear on the Upload page
                dropdown that will be used for EDI generation:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                <li>Go to Settings and open Saved Interchange IDs</li>
                <li>
                  Under Sender IDs or Receiver IDs, enter a friendly label and
                  an Interchange ID (max 15 characters), then click Add
                </li>
                <li>Use the Delete action to remove an ID at any time</li>
                <li>
                  Saved IDs are per-user and immediately available in the Upload
                  page dropdowns
                </li>
              </ol>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Pricing Plan Selection
            </h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Pricing plan section showing two cards: "Good as a Human"
                    (28¢) and "Better than a Human" (35¢) with feature lists
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">Choose between two pricing tiers:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 border border-gray-200 rounded-lg">
                  <h5 className="font-medium mb-2">Good as a Human (28¢)</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Up to 99% accuracy</li>
                    <li>• Basic EDI generation</li>
                    <li>• SFTP and API access</li>
                  </ul>
                </div>
                <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
                  <h5 className="font-medium mb-2">
                    Better than a Human (35¢)
                  </h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Up to 99.9% accuracy</li>
                    <li>• Advanced AI features</li>
                    <li>• Custom ISA sender IDs</li>
                    <li>• Barcode/QR code extraction</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Video Placeholder</h4>
                <p className="text-sm text-blue-700">
                  Screen recording showing the complete Settings workflow:
                  creating SFTP account, updating password, configuring EDI
                  settings, and selecting pricing plan
                </p>
              </div>
            </div>
          </div> */}
        </div>
      ),
    },
    {
      id: "billing",
      title: "Billing & Credits",
      icon: CreditCard,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-3">
              Managing Your Account Balance
            </h3>
            <p className="text-gray-600 mb-4">
              SnapLading uses a prepaid credit system. Purchase credits in
              advance, and they're automatically deducted when documents are
              processed.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Billing Overview</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Billing overview cards showing Current Balance and Pricing
                    information
                  </p>
                </div>
              </div>
            </div> */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h5 className="font-medium">Current Balance Card:</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Shows your available credit balance</li>
                  <li>• Displays in USD currency</li>
                  <li>• Updates in real-time after purchases</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h5 className="font-medium">Pricing Card:</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Standard plan rate per document</li>
                  <li>• Pro plan rate per document</li>
                  <li>• Based on your selected pricing tier</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Purchasing Credits</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Purchase Credits section showing preset amount buttons ($50,
                    $250, $1000) and custom amount input field
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">Two ways to purchase credits:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Quick Purchase:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Preset amounts: $50, $250, $1000</li>
                    <li>• Shows approximate bill count</li>
                    <li>• One-click purchase</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Custom Amount:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Enter any amount ≥ $5.00</li>
                    <li>• Minimum purchase: $5.00</li>
                    <li>• Real-time validation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Payment Process</h4>
            <div className="space-y-3">
              <p className="text-gray-600">
                When you click "Purchase," you'll be redirected to Stripe for
                secure payment processing:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                <li>Click a preset amount or enter custom amount</li>
                <li>Redirected to Stripe checkout page</li>
                <li>Complete payment with credit card</li>
                <li>Automatic redirect back to SnapLading</li>
                <li>Credits added to your account immediately</li>
              </ol>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Transaction History</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Transaction history showing list of deposits and charges
                    with dates, amounts, and balance after each transaction
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">
                View your complete transaction history:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Transaction Types:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>
                      • <span className="text-green-600">Deposit:</span> Credit
                      purchases
                    </li>
                    <li>
                      • <span className="text-red-600">Charge:</span> Document
                      processing fees
                    </li>
                    <li>
                      • <span className="text-blue-600">Refund:</span> Credit
                      refunds
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Information Shown:</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Transaction date and time</li>
                    <li>• Description of transaction</li>
                    <li>• Amount (+ for deposits, - for charges)</li>
                    <li>• Balance after transaction</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Payment Success/Failure Handling
            </h4>
            <div className="space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <h5 className="font-medium text-green-900">
                  Payment Successful
                </h5>
                <p className="text-sm text-green-700">
                  You'll see a success notification and your credits will be
                  immediately available
                </p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">Payment Failed</h5>
                <p className="text-sm text-red-700">
                  Error message will explain the issue (card declined, network
                  error, etc.)
                </p>
              </div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h5 className="font-medium text-yellow-900">
                  Payment Cancelled
                </h5>
                <p className="text-sm text-yellow-700">
                  If you cancel during checkout, you'll return to the billing
                  page with a cancellation message
                </p>
              </div>
            </div>
          </div>

          {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Video Placeholder</h4>
                <p className="text-sm text-blue-700">
                  Screen recording showing the complete billing workflow:
                  checking balance, purchasing credits via Stripe, and viewing
                  transaction history
                </p>
              </div>
            </div>
          </div> */}
        </div>
      ),
    },
    {
      id: "profile",
      title: "Profile Management",
      icon: User,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-3">
              Managing Your Profile
            </h3>
            <p className="text-gray-600 mb-4">
              The Profile page allows you to update personal information and
              change passwords.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Profile Information</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Profile Information card showing form fields for name,
                    email, company, and role badge
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">
                Edit your basic profile information:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>
                  <strong>Full Name:</strong> Display name for your account
                </li>
                <li>
                  <strong>Email Address:</strong> Primary contact email
                </li>
                <li>
                  <strong>Company:</strong> Your organization name
                </li>
                <li>
                  <strong>Role:</strong> Account role (displayed as badge, not
                  editable)
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Account Security</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Account Security card showing password section and "Change
                    Password" button
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">Manage your account security:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                <li>Click "Change Password" to expand password form</li>
                <li>Enter your current password</li>
                <li>Enter and confirm your new password</li>
                <li>Use the eye icon to show/hide passwords</li>
                <li>Click "Update Password" to save changes</li>
              </ol>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Password Change Form</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Expanded password change form showing current password, new
                    password, and confirm password fields with show/hide toggles
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">Password requirements:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Minimum 8 characters long</li>
                <li>Must match in both new password fields</li>
                <li>Current password required for verification</li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Account Information</h4>
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-gray-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">
                    Screenshot Placeholder
                  </h5>
                  <p className="text-sm text-gray-600">
                    Account Information card showing read-only fields: Account
                    ID, Tenant ID, Member Since, Last Login
                  </p>
                </div>
              </div>
            </div> */}
            <div className="space-y-3">
              <p className="text-gray-600">
                View your account details (read-only):
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>
                  <strong>Account ID:</strong> Unique identifier for your
                  account
                </li>
                <li>
                  <strong>Tenant ID:</strong> Organization identifier
                </li>
                <li>
                  <strong>Member Since:</strong> Account creation date
                </li>
                <li>
                  <strong>Last Login:</strong> Most recent login timestamp
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Saving Changes</h4>
            <div className="space-y-3">
              <p className="text-gray-600">
                Use the "Save Changes" button in the top-right corner to save
                all profile updates at once.
              </p>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <h5 className="font-medium text-green-900">Success</h5>
                <p className="text-sm text-green-700">
                  You'll see a success notification when changes are saved
                </p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">Error</h5>
                <p className="text-sm text-red-700">
                  Error messages will appear if validation fails or save
                  operation fails
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting & Support",
      icon: AlertCircle,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-3">
              Common Issues & Solutions
            </h3>
            <p className="text-gray-600 mb-4">
              This section covers common problems and their solutions to help
              you get the most out of SnapLading.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Upload Issues</h4>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: Files won't upload
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">
                    Possible causes and solutions:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>
                      • Check file format (must be JPEG, PNG, BMP, TIFF, or
                      WebP)
                    </li>
                    <li>• Verify file size (very large files may timeout)</li>
                    <li>• Check internet connection</li>
                    <li>• Try refreshing the page and uploading again</li>
                    <li>• Clear browser cache and cookies</li>
                  </ul>
                </div>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: Processing gets stuck
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">
                    If documents show "Processing" for more than 10 minutes:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>• Check the Documents page for status updates</li>
                    <li>• Try uploading a higher quality version</li>
                    <li>• Contact support if the issue persists</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Processing Quality Issues
            </h4>
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h5 className="font-medium text-yellow-900">
                  Problem: Low accuracy or missing data
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-yellow-700">
                    To improve extraction accuracy:
                  </p>
                  <ul className="text-sm text-yellow-700 space-y-1 ml-4">
                    <li>• Use high-resolution images (300 DPI or higher)</li>
                    <li>• Ensure documents are well-lit and not skewed</li>
                    <li>• Avoid shadows or reflections on the document</li>
                    <li>• Make sure all text is clearly visible</li>
                    <li>• Consider upgrading to "Better than a Human" plan</li>
                  </ul>
                </div>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h5 className="font-medium text-yellow-900">
                  Problem: Handwritten text not recognized
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-yellow-700">
                    For handwritten content:
                  </p>
                  <ul className="text-sm text-yellow-700 space-y-1 ml-4">
                    <li>• Ensure handwriting is clear and legible</li>
                    <li>
                      • Use "Better than a Human" plan for improved handwriting
                      recognition
                    </li>
                    <li>• Consider providing typed documents when possible</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              SFTP Connection Issues
            </h4>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: Cannot connect to SFTP server
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">Check these settings:</p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>
                      • Verify server address, port, username, and password
                    </li>
                    <li>• Ensure your firewall allows SFTP connections</li>
                    <li>• Try connecting from a different network</li>
                    <li>• Check if your SFTP account is active in Settings</li>
                  </ul>
                </div>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: SFTP credentials not working
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">
                    If authentication fails:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>• Update your SFTP password in Settings</li>
                    <li>
                      • Copy credentials exactly as shown (no extra spaces)
                    </li>
                    <li>• Wait a few minutes after password changes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Billing & Payment Issues
            </h4>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: Payment declined
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">
                    If your payment is declined:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>• Check with your bank about the transaction</li>
                    <li>• Verify card details are correct</li>
                    <li>• Try a different payment method</li>
                    <li>
                      • Contact your card issuer about international payments
                    </li>
                  </ul>
                </div>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: Credits not added after payment
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">
                    If credits don't appear:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>• Refresh the Billing page</li>
                    <li>• Check your email for payment confirmation</li>
                    <li>• Wait 5-10 minutes for processing</li>
                    <li>• Contact support with your payment reference</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">
              Account & Authentication Issues
            </h4>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: Cannot log in
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">
                    If you can't access your account:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>• Check your email and password</li>
                    <li>• Use the "Forgot Password" link</li>
                    <li>• Clear browser cache and cookies</li>
                    <li>• Try a different browser or incognito mode</li>
                    <li>
                      • Check if you're using the correct login method (Auth0)
                    </li>
                  </ul>
                </div>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-medium text-red-900">
                  Problem: Account initialization stuck
                </h5>
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-red-700">
                    If account setup doesn't complete:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>• Refresh the page and try again</li>
                    <li>• Log out and log back in</li>
                    <li>• Contact support for manual initialization</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Getting Help</h4>
            <div className="space-y-3">
              <p className="text-gray-600">
                If you can't resolve an issue using this guide:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h5 className="font-medium text-blue-900">Contact Support</h5>
                  <p className="text-sm text-blue-700 mt-2">
                    Email: support@snaplading.com
                    <br />
                    Include: Account email, description of issue, and any error
                    messages
                  </p>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h5 className="font-medium text-green-900">
                    Check System Status
                  </h5>
                  <p className="text-sm text-green-700 mt-2">
                    Visit our status page to check for known issues or
                    maintenance windows
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Video Placeholder</h4>
                <p className="text-sm text-blue-700">
                  Screen recording showing common troubleshooting steps:
                  checking upload status, viewing error details, and contacting
                  support
                </p>
              </div>
            </div>
          </div> */}
        </div>
      ),
    },
  ];

  const currentSection = sections.find((s) => s.id === activeSection);

  return (
    <>
      <SEO {...SEOConfigs.documentation} url="/docs" />
      <div className="flex flex-col min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <a href="/" className="text-2xl font-bold gradient-text-purple">
                  SnapLading
                </a>
              </div>
              <div className="flex items-center gap-4">
                <a
                  href="/app/upload"
                  className="px-4 py-2 rounded border border-purple-600 text-white bg-transparent hover:bg-purple-600 hover:text-white transition-colors text-sm font-medium button-purple button-modern"
                >
                  Back to App
                </a>
              </div>
            </div>
          </div>
        </nav>
        <div className="h-16" />

        <div className="container mx-auto px-4 py-8 flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Table of Contents</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <button
                          key={section.id}
                          onClick={() => setActiveSection(section.id)}
                          className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            activeSection === section.id
                              ? "bg-blue-50 text-blue-700 border border-blue-200"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            {section.title}
                          </span>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    {currentSection && (
                      <>
                        <currentSection.icon className="h-6 w-6 text-blue-600" />
                        <CardTitle className="text-2xl">
                          {currentSection.title}
                        </CardTitle>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent>{currentSection?.content}</CardContent>
              </Card>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
